/**
 * EconomyExportPage
 * Unit4-underlag för bokföring och internfakturering från importerade kostnadsrader.
 */

import { useState } from 'react';
import { ROUTES } from '@/constants/routes';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '@/store/languageStore';
import { useAuthStore } from '@/store/authStore';
import { downloadExportBokforing, downloadExportFakturering } from '@/services/vehicleEconomy/vehicleEconomyService';
import OrgTreeSelect from '@/components/common/OrgTreeSelect';
import { DocumentArrowDownIcon, CalendarIcon, ArrowLeftIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import MonthInput from '@/components/common/MonthInput';

const EconomyExportPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const [organizationId, setOrganizationId] = useState(() =>
    user?.organization_id != null ? String(user.organization_id) : ''
  );
  const [period, setPeriod] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });
  const [loadingBokforing, setLoadingBokforing] = useState(false);
  const [loadingFakturering, setLoadingFakturering] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canExport = Boolean(organizationId);

  const handleBokforing = async () => {
    if (!organizationId) return;
    setError(null);
    setLoadingBokforing(true);
    try {
      await downloadExportBokforing(period, organizationId);
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || t('vehicleEconomy.exportError'));
    } finally {
      setLoadingBokforing(false);
    }
  };

  const handleFakturering = async () => {
    if (!organizationId) return;
    setError(null);
    setLoadingFakturering(true);
    try {
      await downloadExportFakturering(period, organizationId);
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || t('vehicleEconomy.exportError'));
    } finally {
      setLoadingFakturering(false);
    }
  };

  return (
    <div className="p-4 max-w-2xl">
      <div className="flex items-center gap-3 mb-2">
        <button onClick={() => navigate(ROUTES.SYSTEM_FLEET)} className="p-2 hover:bg-bgElevated rounded-lg transition-colors">
          <ArrowLeftIcon className="w-5 h-5 text-textDefault" />
        </button>
        <h1 className="text-2xl font-bold text-textDefault flex items-center gap-2">
          <ArrowDownTrayIcon className="w-6 h-6 text-primary" />
          {t('vehicleEconomy.exportTitle')}
        </h1>
      </div>
      <p className="text-sm text-textSecondary mb-6">
        {t('vehicleEconomy.exportDescription')}
      </p>

      {error && (
        <div className="mb-4 rounded bg-red-50 p-3 text-sm text-red-800">
          {error}
        </div>
      )}

      <div className="mb-6 grid grid-cols-1 gap-4">
        <div>
          <label className="block text-sm font-medium text-textDefault mb-1">
            {t('common.organization')}
          </label>
          <OrgTreeSelect
            value={organizationId}
            onChange={setOrganizationId}
            placeholder={t('common.selectOrganization')}
          />
        </div>
        <label className="flex items-center gap-2 text-sm font-medium text-textDefault">
          <CalendarIcon className="h-5 w-5" />
          {t('vehicleEconomy.period')}
          <MonthInput
            className="rounded border border-gray-300 px-3 py-2 text-sm"
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
          />
        </label>
      </div>

      {!canExport && (
        <p className="mb-4 text-sm text-textSecondary">{t('vehicleEconomy.exportSelectOrg')}</p>
      )}

      <div className="flex flex-col gap-4">
        <button
          type="button"
          onClick={handleBokforing}
          disabled={!canExport || loadingBokforing}
          className="flex items-center gap-2 rounded-lg border border-border bg-bgElevated px-4 py-3 text-left hover:bg-bgHover disabled:opacity-50"
        >
          <DocumentArrowDownIcon className="h-6 w-6 text-bgPrimary" />
          <div>
            <span className="font-medium">{t('vehicleEconomy.exportBokforing')}</span>
            <p className="text-xs text-textSecondary mt-0.5">
              {t('vehicleEconomy.exportBokforingDesc')}
            </p>
          </div>
        </button>

        <button
          type="button"
          onClick={handleFakturering}
          disabled={!canExport || loadingFakturering}
          className="flex items-center gap-2 rounded-lg border border-border bg-bgElevated px-4 py-3 text-left hover:bg-bgHover disabled:opacity-50"
        >
          <DocumentArrowDownIcon className="h-6 w-6 text-bgPrimary" />
          <div>
            <span className="font-medium">{t('vehicleEconomy.exportFakturering')}</span>
            <p className="text-xs text-textSecondary mt-0.5">
              {t('vehicleEconomy.exportFaktureringDesc')}
            </p>
          </div>
        </button>
      </div>
    </div>
  );
};

export default EconomyExportPage;
